import type {DictamenAptitud, DictamenActual} from '../types/portalCliente';
import type {Empleado} from '../types/empleado';

export interface DictamenBadgeStyle {
    background: string;
    color: string;
    border: string;
}

export const DICTAMEN_BADGE_STYLES: Record<DictamenAptitud, DictamenBadgeStyle> = {
    apto: {
        background: '#f0fdf4',
        color: '#15803d',
        border: '#bbf7d0'
    },
    apto_restricciones: {
        background: '#fffbeb',
        color: '#b45309',
        border: '#fde68a'
    },
    no_apto: {
        background: '#fef2f2',
        color: '#b91c1c',
        border: '#fecaca'
    },
    pendiente: {
        background: '#eff6ff',
        color: '#1d4ed8',
        border: '#bfdbfe'
    },
    sin_certificado: {
        background: '#f9fafb',
        color: '#4b5563',
        border: '#e5e7eb'
    }
};

export const DICTAMEN_LABELS: Record<DictamenAptitud, string> = {
    apto: 'Apto',
    apto_restricciones: 'Apto con restricciones',
    no_apto: 'No apto',
    pendiente: 'Pendiente',
    sin_certificado: 'Sin certificado'
};

export const DICTAMEN_FILTER_OPTIONS: Array<{value: DictamenAptitud | ''; label: string}> = [
    {value: '', label: 'Todos'},
    {value: 'apto', label: 'Apto'},
    {value: 'apto_restricciones', label: 'Apto con Restricciones'},
    {value: 'no_apto', label: 'No Apto'},
    {value: 'pendiente', label: 'Pendiente'},
    {value: 'sin_certificado', label: 'Sin Certificado'}
];

const DICTAMEN_ALIASES: Record<string, DictamenAptitud> = {
    apto: 'apto',
    apto_restricciones: 'apto_restricciones',
    apto_con_restricciones: 'apto_restricciones',
    'apto con restricciones': 'apto_restricciones',
    no_apto: 'no_apto',
    'no apto': 'no_apto',
    pendiente: 'pendiente',
    sin_certificado: 'sin_certificado',
    'sin certificado': 'sin_certificado'
};

export const normalizeDictamenAptitud = (value: unknown): DictamenAptitud => {
    const raw = String(value ?? '')
        .trim()
        .toLowerCase();

    if (!raw) {
        return 'sin_certificado';
    }

    if (DICTAMEN_ALIASES[raw]) {
        return DICTAMEN_ALIASES[raw];
    }

    if (raw in DICTAMEN_BADGE_STYLES) {
        return raw as DictamenAptitud;
    }

    return 'sin_certificado';
};

const formatEstatusLabel = (estatus: DictamenAptitud): string =>
    DICTAMEN_LABELS[estatus] || estatus.replace(/_/g, ' ');

export const resolveDictamenFromEmpleado = (empleado: Empleado): DictamenActual => {
    const rawDictamen = empleado.dictamen_actual as DictamenActual | string | null | undefined;

    if (typeof rawDictamen === 'string') {
        const estatus = normalizeDictamenAptitud(rawDictamen);

        return {
            estatus,
            etiqueta: formatEstatusLabel(estatus)
        };
    }

    if (rawDictamen && typeof rawDictamen === 'object') {
        const record = rawDictamen as unknown as Record<string, unknown>;
        const estatus = normalizeDictamenAptitud(
            record.estatus ?? record.dictamen ?? record.dictamen_actual
        );

        return {
            estatus,
            etiqueta:
                String(record.etiqueta ?? record.label ?? '').trim() || formatEstatusLabel(estatus),
            fecha_emision:
                (record.fecha_emision as string | null | undefined) ??
                (record.fechaEmision as string | null | undefined) ??
                null,
            fecha_vencimiento:
                (record.fecha_vencimiento as string | null | undefined) ??
                (record.fechaVencimiento as string | null | undefined) ??
                null,
            restricciones_laborales:
                (record.restricciones_laborales as string | null | undefined) ?? null
        };
    }

    const cert = empleado.ultimo_certificado;

    if (!cert) {
        return {
            estatus: 'sin_certificado',
            etiqueta: DICTAMEN_LABELS.sin_certificado
        };
    }

    const estatus = normalizeDictamenAptitud(cert.estatus_aptitud);

    return {
        estatus,
        etiqueta: cert.etiqueta_aptitud?.trim() || formatEstatusLabel(estatus),
        fecha_emision: cert.fecha_emision,
        fecha_vencimiento: cert.fecha_vencimiento
    };
};

export const formatDictamenLabel = (dictamen: DictamenActual | null | undefined): string => {
    if (!dictamen) {
        return DICTAMEN_LABELS.sin_certificado;
    }

    const estatus = normalizeDictamenAptitud(dictamen.estatus);
    const etiqueta = String(dictamen.etiqueta ?? '').trim();

    return etiqueta || formatEstatusLabel(estatus);
};

/** Valor canónico para query params del API. */
export const toApiDictamen = (value: DictamenAptitud | '' | null | undefined): string => {
    if (!value) {
        return '';
    }

    return normalizeDictamenAptitud(value);
};
